"use client";

import { ArrowUpRight } from "lucide-react";
import Image from "next/image";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import { LEAD_DROP_VARIANTS } from "@/constants/leadConstants";
import { FireWidgetViewedEvent } from "@/customAnalytics/trackingEvents";
import { formatSectionTitle } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";

interface SalaryItem {
  meta_id?: number;
  meta_salary_package: string;
  meta_package_description?: string;
  meta_priority?: number;
  meta_status?: boolean;
}

interface AcademicDeliverables1002Data {
  title?: string;
  title_text_color?: string;
  sub_title?: string;
  sub_title_text_color?: string;
  recruiters_title?: string;
  recruiters_sub_title?: string;
  button_text?: string;
  button_text_color?: string;
  button_background_color?: string;
  desktop_bg_image?: string;
  mobile_bg_image?: string;
  alt_image?: string;
  metadata?: SalaryItem[];
  status?: boolean;
}

const AcademicDeliverables1002 = ({ data }: { data: any }) => {
  const dispatch = useDispatch();
  const elementRef = useRef<HTMLElement>(null);
  const [isHovered, setIsHovered] = useState(false);

  // Handle both single and double nesting from PreviewRenderer
  const sectionData: AcademicDeliverables1002Data = data?.data ?? data ?? {};

  const {
    title = "",
    title_text_color = "#000000",
    sub_title = "",
    sub_title_text_color = "#000000",
    recruiters_title = "",
    recruiters_sub_title = "",
    button_text = "Get Started",
    button_text_color = "#FFFFFF",
    button_background_color = "",
    desktop_bg_image = "",
    mobile_bg_image = "",
    alt_image = "",
    metadata = [],
  } = sectionData;

  // Active salary rows, sorted by priority (mirrors AcademicDeliverables2's
  // metadata handling).
  const salary = useMemo(() => {
    if (!metadata || metadata.length === 0) return [];
    return [...metadata]
      .filter((item) => item.meta_status)
      .sort((a, b) => (a.meta_priority ?? 0) - (b.meta_priority ?? 0));
  }, [metadata]);

  useEffect(() => {
    if (elementRef.current) {
      FireWidgetViewedEvent(elementRef, "placement_success");
    }
  }, []);

  if (!sectionData || !sectionData.status) {
    return null;
  }

  const hasTopContent = Boolean(title || sub_title || salary.length > 0);

  return (
    <>
      {hasTopContent && (
        <section
          ref={elementRef}
          className="flex flex-col items-center justify-center w-full mx-auto py-[50px] min-[768px]:py-[70px] px-0 min-[1024px]:px-[122px]"
        >
          <div className="min-[1024px]:w-screen flex flex-col items-center justify-center">
            <div className="min-[1024px]:max-w-[1200px] w-full flex flex-col items-center justify-center mx-auto px-0 min-[768px]:px-5">
              {title && (
                <h2
                  className="text-[28px] w-[320px] min-[768px]:w-[500px] min-[1024px]:w-full min-[768px]:text-[36px] text-center font-poppins font-semibold leading-[126%] tracking-[-0.816px] mb-[16px] mx-auto"
                  style={{ color: title_text_color }}
                  dangerouslySetInnerHTML={{
                    __html: formatSectionTitle(title, "text-primary"),
                  }}
                />
              )}

              {sub_title && (
                <p
                  className="text-[14px] font-poppins font-normal leading-[22px] text-center w-[302px] min-[768px]:w-auto min-[768px]:max-w-[680px] mx-auto mb-8 min-[768px]:mb-[66px] min-[768px]:text-base min-[768px]:leading-relaxed"
                  style={{ color: sub_title_text_color }}
                >
                  {sub_title}
                </p>
              )}

              {salary.length > 0 && (
                <div className="flex flex-col min-[768px]:flex-row justify-around items-center min-[1024px]:gap-[60px] min-[768px]:gap-[20px] gap-[60px] min-[768px]:w-[780px]">
                  {salary.map((item, index) => (
                    <div
                      key={item.meta_id || `salary-${index}`}
                      className="flex flex-col min-[768px]:w-[220px] items-center"
                    >
                      <div className="text-[44px] font-bold text-primary">
                        {item.meta_salary_package}
                      </div>
                      <div className="text-sm min-[768px]:text-[14px] leading-[18px] font-normal text-black text-center">
                        {item.meta_package_description}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Second band — hiring partners on a pure-white background (the theme's
          `bg-white` token is #f8f9fa, so the hex is intentional). */}
      {(desktop_bg_image || mobile_bg_image) && (
        <section
          ref={hasTopContent ? undefined : elementRef}
          className="bg-[#FFFFFF] flex flex-col items-center justify-center w-full mx-auto py-6 min-[768px]:py-[38px]"
        >
          <div className="px-0 w-full">
            <div className="text-center flex flex-col items-center justify-center gap-[16px] py-[16px]">
              {recruiters_title && (
                <h2
                  className="text-[28px] text-black min-[1024px]:text-[32px] text-center font-poppins font-semibold leading-[126%] tracking-[-0.816px]"
                  dangerouslySetInnerHTML={{
                    __html: formatSectionTitle(
                      recruiters_title,
                      "text-primary",
                    ),
                  }}
                />
              )}
              {recruiters_sub_title && (
                <p className="text-[14px] text-black font-poppins font-normal leading-[22px] text-center w-[302px] min-[768px]:w-auto min-[768px]:max-w-[680px] mx-auto">
                  {recruiters_sub_title}
                </p>
              )}

              <button
                type="button"
                onClick={() => {
                  dispatch(
                    handleOpenLeaddrop({
                      variant: LEAD_DROP_VARIANTS.REGISTER,
                    }),
                  );
                }}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                className="inline-flex items-center justify-center gap-2.5 whitespace-nowrap rounded-md bg-primary px-[18px] py-3 text-[16px] font-semibold cursor-pointer"
                // Empty background = keep the theme's bg-primary.
                style={{
                  color: button_text_color,
                  backgroundColor: button_background_color || undefined,
                }}
              >
                {button_text || "Get Started"}
                <span className="size-6">
                  <ArrowUpRight
                    className={
                      isHovered
                        ? "rotate-[45deg] transition-transform duration-300"
                        : "rotate-0 transition-transform duration-300"
                    }
                  />
                </span>
              </button>
            </div>

            <div className="w-full mb-6">
              <div className="w-full mx-auto">
                {desktop_bg_image && (
                  <div className="hidden min-[768px]:block">
                    <Image
                      src={desktop_bg_image}
                      alt={alt_image || "Hiring Partner Companies"}
                      width={1120}
                      height={288}
                      className="object-contain w-full"
                    />
                  </div>
                )}

                {mobile_bg_image && (
                  <div className="block min-[768px]:hidden w-full">
                    <Image
                      src={mobile_bg_image}
                      alt={alt_image || "Hiring Partner Companies"}
                      width={375}
                      height={400}
                      className="w-full h-auto object-contain px-0"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}
    </>
  );
};

export default AcademicDeliverables1002;
